import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  HardDrive,
  Upload,
  Trash2,
  Copy,
  Check,
  RefreshCw,
  CheckSquare,
  Square,
  Package,
  X,
  FileBox,
  Hash,
  AlertCircle,
  Database,
  Users,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import weightService from '../services/weightService';
import * as imageService from '../services/imageService';
import { useAuth } from '../hooks/useAuth';

// ── helpers ──────────────────────────────────────────────────────────

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const truncHash = (hash) => hash ? `${hash.substring(0, 8)}...${hash.substring(hash.length - 6)}` : '';

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// ── component ────────────────────────────────────────────────────────

const StoragePage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('weights'); // 'weights' | 'admin'

  const [weights, setWeights] = useState([]);
  const [usage, setUsage] = useState(null);
  const [curatedImages, setCuratedImages] = useState([]);
  const [filterImageId, setFilterImageId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [copied, setCopied] = useState(null);

  // Upload state
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadImageId, setUploadImageId] = useState('');
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  // Admin state
  const [adminBlobs, setAdminBlobs] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [expandedBlob, setExpandedBlob] = useState(null);

  // ── data fetching ────────────────────────────────────────────────

  const fetchWeights = useCallback(async () => {
    setLoading(true);
    try {
      const res = await weightService.listWeights({
        curatedImageId: filterImageId || undefined,
        limit: 500,
      });
      if (res.success) setWeights(res.weights || []);
    } catch (err) {
      console.error('Fetch weights error:', err);
    } finally {
      setLoading(false);
    }
  }, [filterImageId]);

  const fetchUsage = useCallback(async () => {
    try {
      const res = await weightService.getUsage();
      if (res.success) setUsage(res.usage);
    } catch (err) {
      console.error('Fetch usage error:', err);
    }
  }, []);

  const fetchCuratedImages = useCallback(async () => {
    try {
      const res = await imageService.listCuratedImages();
      if (res.success) setCuratedImages(res.images || []);
    } catch (err) {
      console.error('Fetch curated images error:', err);
    }
  }, []);

  const fetchAdminBlobs = useCallback(async () => {
    setAdminLoading(true);
    try {
      const res = await weightService.adminListBlobs({ limit: 200 });
      if (res.success) {
        setAdminBlobs(res.blobs || []);
        setAdminStats(res.stats || null);
      }
    } catch (err) {
      console.error('Fetch admin blobs error:', err);
    } finally {
      setAdminLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWeights();
    fetchUsage();
    fetchCuratedImages();
  }, [fetchWeights, fetchUsage, fetchCuratedImages]);

  useEffect(() => {
    if (activeTab === 'admin' && user?.isAdmin) {
      fetchAdminBlobs();
    }
  }, [activeTab, user, fetchAdminBlobs]);

  // ── upload ───────────────────────────────────────────────────────

  const openUploadModal = () => {
    setUploadModal(true);
    setUploadFile(null);
    setUploadImageId('');
    setUploadStatus(null);
    setUploadProgress(0);
    setUploadResult(null);
    setUploadError(null);
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploadError(null);
    setUploadResult(null);

    const result = await weightService.uploadWeight(uploadFile, {
      curatedImageId: uploadImageId ? parseInt(uploadImageId, 10) : null,
      onHashProgress: (pct) => setUploadProgress(pct),
      onUploadProgress: (pct) => setUploadProgress(pct),
      onStatus: (s) => setUploadStatus(s),
    });

    if (result.success) {
      setUploadStatus(result.deduplicated ? 'deduplicated' : 'done');
      setUploadResult(result);
      await fetchWeights();
      await fetchUsage();
    } else {
      setUploadStatus('error');
      setUploadError(result.error);
    }
  };

  // ── actions ──────────────────────────────────────────────────────

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(url);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const handleDelete = async (ids) => {
    if (!window.confirm(`Delete ${ids.length} weight(s)?`)) return;
    for (const id of ids) {
      await weightService.deleteWeight(id);
    }
    setSelected(new Set());
    await fetchWeights();
    await fetchUsage();
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === weights.length) setSelected(new Set());
    else setSelected(new Set(weights.map(w => w.id)));
  };

  const statusLabels = {
    hashing: 'Computing SHA-256 hash...',
    checking: 'Checking for duplicates...',
    uploading: 'Uploading...',
    completing: 'Finalizing...',
    deduplicated: 'File already exists!',
    done: 'Upload complete!',
    error: 'Upload failed',
  };

  // ── render ───────────────────────────────────────────────────────

  return (
    <div className="max-w-7xl mx-auto mt-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <HardDrive size={28} className="text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Model Weights</h1>
            <p className="text-sm text-gray-500">Upload and manage model weights for your curated images</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => { fetchWeights(); if (activeTab === 'admin') fetchAdminBlobs(); }}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
            title="Refresh"
          >
            <RefreshCw size={18} className={loading || adminLoading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={openUploadModal}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <Upload size={16} />
            <span>Upload Weight</span>
          </button>
        </div>
      </div>

      {/* Tabs (admin gets extra tab) */}
      {user?.isAdmin && (
        <div className="flex space-x-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
          <button
            onClick={() => setActiveTab('weights')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'weights' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center space-x-1.5">
              <Package size={14} />
              <span>My Weights</span>
            </span>
          </button>
          <button
            onClick={() => setActiveTab('admin')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'admin' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center space-x-1.5">
              <Database size={14} />
              <span>Stored Blobs (Admin)</span>
            </span>
          </button>
        </div>
      )}

      {/* ── My Weights Tab ─────────────────────────────────────── */}
      {activeTab === 'weights' && (
        <>
          {/* Usage bar */}
          {usage && (
            <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Storage: {usage.totalFormatted} / {usage.quotaFormatted}
                </span>
                <span className="text-sm text-gray-500">{usage.weightCount} weight(s)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    usage.usagePercent > 90 ? 'bg-red-500' : usage.usagePercent > 70 ? 'bg-yellow-500' : 'bg-purple-600'
                  }`}
                  style={{ width: `${Math.min(usage.usagePercent, 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Filter + batch actions */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <select
                value={filterImageId || ''}
                onChange={(e) => setFilterImageId(e.target.value ? parseInt(e.target.value, 10) : null)}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-700"
              >
                <option value="">All models</option>
                {curatedImages.map(img => (
                  <option key={img.id} value={img.id}>{img.name}</option>
                ))}
              </select>
            </div>

            {selected.size > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">{selected.size} selected</span>
                <button
                  onClick={() => handleDelete(Array.from(selected))}
                  className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 border border-red-200"
                >
                  <Trash2 size={14} />
                  <span>Delete</span>
                </button>
                <button onClick={() => setSelected(new Set())} className="p-1.5 text-gray-400 hover:text-gray-600 rounded">
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Weights table */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="grid grid-cols-[40px_1fr_150px_100px_100px_120px_80px] gap-2 px-4 py-3 border-b bg-gray-50 text-xs font-medium text-gray-500 uppercase">
              <div>
                <button onClick={selectAll} className="text-gray-400 hover:text-gray-600">
                  {selected.size === weights.length && weights.length > 0 ? <CheckSquare size={16} /> : <Square size={16} />}
                </button>
              </div>
              <div>Filename</div>
              <div>Model</div>
              <div>Size</div>
              <div>Hash</div>
              <div>Date</div>
              <div className="text-right">Actions</div>
            </div>

            {loading ? (
              <div className="p-12 text-center text-gray-400">
                <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                <p>Loading weights...</p>
              </div>
            ) : weights.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <FileBox size={40} className="mx-auto mb-3 text-gray-300" />
                <p className="text-lg font-medium text-gray-500 mb-1">No weights uploaded</p>
                <p className="text-sm">Upload model weights (LoRAs, checkpoints, VAE) to use in your workflows</p>
              </div>
            ) : (
              weights.map(w => {
                const isSelected = selected.has(w.id);
                return (
                  <div
                    key={w.id}
                    className={`grid grid-cols-[40px_1fr_150px_100px_100px_120px_80px] gap-2 px-4 py-3 border-b last:border-0 items-center hover:bg-gray-50 ${
                      isSelected ? 'bg-purple-50' : ''
                    }`}
                  >
                    <div>
                      <button onClick={() => toggleSelect(w.id)} className="text-gray-400 hover:text-gray-600">
                        {isSelected ? <CheckSquare size={16} className="text-purple-600" /> : <Square size={16} />}
                      </button>
                    </div>
                    <div className="flex items-center space-x-2 min-w-0">
                      <Package size={16} className="text-gray-400 shrink-0" />
                      <span className="text-sm font-medium text-gray-800 truncate" title={w.filename}>
                        {w.display_name || w.filename}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 truncate">{w.curated_image_name || '—'}</div>
                    <div className="text-xs text-gray-500">{w.sizeFormatted}</div>
                    <div className="flex items-center space-x-1">
                      <Hash size={12} className="text-gray-400" />
                      <span className="text-xs text-gray-500 font-mono" title={w.sha256_hash}>
                        {truncHash(w.sha256_hash)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">{timeAgo(w.created_at)}</div>
                    <div className="flex items-center justify-end space-x-1">
                      <button onClick={() => copyUrl(w.url)} className="p-1 text-gray-400 hover:text-purple-600 rounded" title="Copy r2:// URL">
                        {copied === w.url ? <Check size={15} className="text-green-500" /> : <Copy size={15} />}
                      </button>
                      <button onClick={() => handleDelete([w.id])} className="p-1 text-gray-400 hover:text-red-600 rounded" title="Delete">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {/* ── Admin R2 Blobs Tab ─────────────────────────────────── */}
      {activeTab === 'admin' && user?.isAdmin && (
        <>
          {/* Global stats */}
          {adminStats && (
            <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
              <div className="flex items-center space-x-6">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Total Stored Objects</p>
                  <p className="text-xl font-bold text-gray-900">{adminStats.blobCount}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Total Storage</p>
                  <p className="text-xl font-bold text-gray-900">{adminStats.totalFormatted}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="grid grid-cols-[40px_1fr_100px_100px_80px_120px] gap-2 px-4 py-3 border-b bg-gray-50 text-xs font-medium text-gray-500 uppercase">
              <div></div>
              <div>Filename / SHA-256</div>
              <div>Size</div>
              <div>Refs</div>
              <div>Users</div>
              <div>Date</div>
            </div>

            {adminLoading ? (
              <div className="p-12 text-center text-gray-400">
                <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                <p>Loading blobs...</p>
              </div>
            ) : adminBlobs.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <Database size={40} className="mx-auto mb-3 text-gray-300" />
                <p className="text-lg font-medium text-gray-500">No blobs stored</p>
              </div>
            ) : (
              adminBlobs.map(blob => {
                const isExpanded = expandedBlob === blob.id;
                const uniqueUsers = [...new Set(blob.references.map(r => r.username))];
                return (
                  <div key={blob.id} className="border-b last:border-0">
                    <div
                      className="grid grid-cols-[40px_1fr_100px_100px_80px_120px] gap-2 px-4 py-3 items-center hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedBlob(isExpanded ? null : blob.id)}
                    >
                      <div className="text-gray-400">
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{blob.filename}</p>
                        <p className="text-xs font-mono text-gray-400 truncate" title={blob.sha256_hash}>
                          {blob.sha256_hash}
                        </p>
                      </div>
                      <div className="text-xs text-gray-500">{blob.sizeFormatted}</div>
                      <div className="text-xs text-gray-500">{blob.ref_count} ref{blob.ref_count !== 1 ? 's' : ''}</div>
                      <div className="flex items-center space-x-1">
                        <Users size={12} className="text-gray-400" />
                        <span className="text-xs text-gray-500">{uniqueUsers.length}</span>
                      </div>
                      <div className="text-xs text-gray-500">{timeAgo(blob.created_at)}</div>
                    </div>

                    {/* Expanded: user references */}
                    {isExpanded && (
                      <div className="px-4 pb-4 pl-14 bg-gray-50">
                        <p className="text-xs font-medium text-gray-500 uppercase mb-2">References</p>
                        <div className="space-y-2">
                          {blob.references.map((ref, i) => (
                            <div key={i} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border text-xs">
                              <div className="flex items-center space-x-3">
                                <span className="font-medium text-gray-800">{ref.username}</span>
                                <span className="text-gray-400">{ref.email}</span>
                                {ref.curatedImageName && (
                                  <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px]">
                                    {ref.curatedImageName}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-gray-400 truncate max-w-[200px]" title={ref.displayName}>
                                  {ref.displayName}
                                </span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); copyUrl(`r2://${blob.sha256_hash}/${ref.displayName}`); }}
                                  className="p-1 text-gray-400 hover:text-purple-600 rounded"
                                  title="Copy r2:// URL"
                                >
                                  {copied === `r2://${blob.sha256_hash}/${ref.displayName}` ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-2 text-xs text-gray-400">
                          Storage key: <code className="bg-gray-200 px-1 rounded">{blob.r2_key}</code>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {/* ── Upload Modal ───────────────────────────────────────── */}
      {uploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => !uploadStatus && setUploadModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Upload Model Weight</h2>
              <button
                onClick={() => setUploadModal(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={uploadStatus === 'uploading' || uploadStatus === 'hashing'}
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Curated Model (optional)</label>
                <select
                  value={uploadImageId}
                  onChange={(e) => setUploadImageId(e.target.value)}
                  disabled={!!uploadStatus}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">No model association</option>
                  {curatedImages.map(img => (
                    <option key={img.id} value={img.id}>{img.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">Associate this weight with a specific model image</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weight File</label>
                {!uploadFile ? (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-400 hover:bg-purple-50 transition-colors"
                  >
                    <Upload size={32} className="mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-gray-500">Click to select a file</p>
                    <p className="text-xs text-gray-400 mt-1">.safetensors, .ckpt, .pt, .bin, .onnx, etc.</p>
                  </button>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{uploadFile.name}</p>
                      <p className="text-xs text-gray-500">{formatBytes(uploadFile.size)}</p>
                    </div>
                    {!uploadStatus && (
                      <button onClick={() => setUploadFile(null)} className="text-gray-400 hover:text-gray-600 ml-2">
                        <X size={16} />
                      </button>
                    )}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".safetensors,.ckpt,.pt,.pth,.bin,.onnx,.sft,.engine,.patch"
                  onChange={(e) => { if (e.target.files[0]) setUploadFile(e.target.files[0]); }}
                />
              </div>

              {uploadStatus && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">
                      {statusLabels[uploadStatus] || uploadStatus}
                    </span>
                    {(uploadStatus === 'hashing' || uploadStatus === 'uploading') && (
                      <span className="text-sm text-gray-500">{Math.round(uploadProgress * 100)}%</span>
                    )}
                  </div>
                  {(uploadStatus === 'hashing' || uploadStatus === 'uploading') && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="h-2 rounded-full bg-purple-600 transition-all" style={{ width: `${uploadProgress * 100}%` }} />
                    </div>
                  )}
                  {uploadStatus === 'checking' && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="h-2 rounded-full bg-purple-600 animate-pulse w-full" />
                    </div>
                  )}
                </div>
              )}

              {uploadResult && (
                <div className={`p-3 rounded-lg ${uploadResult.deduplicated ? 'bg-blue-50 border border-blue-200' : 'bg-green-50 border border-green-200'}`}>
                  <p className="text-sm font-medium mb-2">
                    {uploadResult.deduplicated ? 'File already existed — linked to your account' : 'Upload successful!'}
                  </p>
                  <div className="flex items-center space-x-2">
                    <code className="flex-1 text-xs font-mono bg-white px-2 py-1 rounded border truncate">
                      {uploadResult.url}
                    </code>
                    <button onClick={() => copyUrl(uploadResult.url)} className="p-1.5 text-gray-500 hover:text-purple-600 rounded">
                      {copied === uploadResult.url ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Use this URL in your workflow JSON, predict.py, or inference inputs
                  </p>
                </div>
              )}

              {uploadError && (
                <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle size={16} className="text-red-500 shrink-0" />
                  <p className="text-sm text-red-700">{uploadError}</p>
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t bg-gray-50 flex justify-end space-x-3">
              <button onClick={() => setUploadModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                {uploadResult ? 'Close' : 'Cancel'}
              </button>
              {!uploadResult && !uploadError && (
                <button
                  onClick={handleUpload}
                  disabled={!uploadFile || !!uploadStatus}
                  className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {uploadStatus ? 'Processing...' : 'Upload'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoragePage;
